import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Button, FormControl, Table, Spinner, Modal, Form } from "react-bootstrap";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData } from "../../store/Functions";
import { useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ExportButtons from "../../components/ExportButtons";
import { useSignalRNotifications } from "../../context/SignalRNotificationContext";

const AssignDriver = () => {
  // State management
  const [state, setState] = useState({});
  const [gridData, setGridData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [driverVehicleDetails, setDriverVehicleDetails] = useState([]);
  const [selectedStatusId, setSelectedStatusId] = useState("0");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const location = useLocation();
  const { subscribeToNewBookings, isConnected } = useSignalRNotifications();
  const [newlyAddedId, setNewlyAddedId] = useState(null);
  const breadCrumbTitle = "Booking Report";
  const breadcrumbItem = "Booking Report";

  const API_URL = `${API_WEB_URLS.MASTER}/0/token/BookingMaster`;
  const API_CANCEL_URL = `${API_WEB_URLS.MASTER}/0/token/CancelBookingMaster`;
  const API_URL1 = `${API_WEB_URLS.MASTER}/0/token/StatusMaster`;
  const API_URL2 = `${API_WEB_URLS.MASTER}/0/token/DriverVehicleDetails`;
  const API_URL3 = `${API_WEB_URLS.MASTER}/0/token/UpdateVehicleMaster`;

  const fetchTableData = useCallback(
    async (ID) => {
      await Fn_FillListData(dispatch, setGridData, "gridData", `${API_URL}/Id/${ID}`);
    },
    [dispatch, API_URL]
  );

  const driverOptions = useMemo(() => driverVehicleDetails || [], [driverVehicleDetails]);
  const selectedDriverDetails = useMemo(() => {
    if (!selectedDriverId) {
      return undefined;
    }
    return driverOptions.find((driver) => String(driver.Id) === String(selectedDriverId));
  }, [driverOptions, selectedDriverId]);

  const btnAssignDriverOnClick = useCallback(
    (booking) => {
      setSelectedBooking(booking);
      const defaultDriverId =
        driverOptions && driverOptions.length > 0 ? String(driverOptions[0].Id) : "";
      setSelectedDriverId(defaultDriverId);
      setShowAssignModal(true);
    },
    [driverOptions]
  );

  const btnCancelOnClick = useCallback(async (booking) => {
    console.log("Cancel booking:", booking);
    await Fn_FillListData(dispatch, setState, "nothing", `${API_CANCEL_URL}/Id/${booking.Id}`);
    await fetchTableData(selectedStatusId);
  }, [dispatch, API_CANCEL_URL, fetchTableData, selectedStatusId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchTableData("0");
        await Fn_FillListData(dispatch, setStatusData, "gridData", `${API_URL1}/Id/0`);
        await Fn_FillListData(dispatch, setDriverVehicleDetails, "gridData", `${API_URL2}/Id/0`);
        setSelectedStatusId("0");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, API_URL, API_URL1, API_URL2, fetchTableData]);

  const handleStatusFilterChange = useCallback(
    async (event) => {
      const { value } = event.target;
      setSelectedStatusId(value);
      setLoading(true);
      try {
        await fetchTableData(value);
      } finally {
        setLoading(false);
      }
    },
    [fetchTableData]
  );

  const data = useMemo(() => gridData || [], [gridData]);
  const statusOptions = useMemo(() => statusData || [], [statusData]);

  // Subscribe to real-time SignalR bookings for live auto-refresh
  useEffect(() => {
    const unsubscribe = subscribeToNewBookings((newBooking) => {
      console.log("⚡ [AssignDriver] Live booking received via SignalR. Refreshing grid...", newBooking);
      fetchTableData(selectedStatusId || "0");
      if (newBooking?.BookingId) {
        setNewlyAddedId(String(newBooking.BookingId));
        setTimeout(() => setNewlyAddedId(null), 10000);
      }
    });
    return () => unsubscribe();
  }, [subscribeToNewBookings, fetchTableData, selectedStatusId]);

  // When arriving from topbar notification click, highlight booking and open assign modal if available
  useEffect(() => {
    if (location.state?.highlightBookingId && gridData && gridData.length > 0) {
      const targetId = String(location.state.highlightBookingId);
      setNewlyAddedId(targetId);
      const match = gridData.find((b) => String(b.Id) === targetId);
      if (match) {
        btnAssignDriverOnClick(match);
      }
    }
  }, [location.state, gridData, btnAssignDriverOnClick]);

  const handleAssignModalClose = useCallback(() => {
    setShowAssignModal(false);
    setSelectedBooking(null);
    setSelectedDriverId("");
  }, []);

  const handleAssignConfirm = useCallback(async () => {
    if (!selectedBooking || !selectedDriverId) {
      return;
    }
console.log(selectedBooking);
    await Fn_FillListData(
      dispatch,
      setGridData,
      "gridData",
      `${API_URL3}/${selectedBooking.Id}/${selectedDriverId}`
    );

    await fetchTableData(selectedStatusId);

    setShowAssignModal(false);
    setSelectedBooking(null);
    setSelectedDriverId("");
  }, [API_URL3, dispatch, fetchTableData, selectedBooking, selectedDriverId, selectedStatusId]);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "Id",
        Cell: ({ value }) => (
          <span className="text-muted small fw-semibold">{value || "-"}</span>
        ),
      },
      {
        Header: "Pickup",
        accessor: "PickupAddress",
        Cell: ({ row }) => (
          <div style={{ minWidth: "220px" }}>
            <div className="d-flex align-items-start mb-1">
              <i className="fas fa-map-marker-alt me-2 text-primary mt-1" style={{ fontSize: "0.85rem" }}></i>
              <span className="fw-semibold text-wrap" style={{ fontSize: "0.82rem" }}>
                {row.original.PickupAddress || "-"}
              </span>
            </div>
            {(row.original.SenderName || row.original.SenderMobile) && (
              <div className="d-flex align-items-center mt-1">
                <i className="fas fa-user me-2 text-secondary" style={{ fontSize: "0.75rem" }}></i>
                <small className="text-muted">
                  {row.original.SenderName || "-"}{" "}
                  {row.original.SenderMobile && (
                    <span className="text-dark fw-semibold">· {row.original.SenderMobile}</span>
                  )}
                </small>
              </div>
            )}
            <div className="d-flex align-items-center mt-1">
              <i className="fas fa-location-arrow me-2 text-info" style={{ fontSize: "0.7rem" }}></i>
              <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                {row.original.PickupLatitude || "-"}, {row.original.PickupLongitude || "-"}
              </small>
            </div>
            {row.original.Stop1Photo && (
              <div className="mt-2">
                <a
                  href={`${API_WEB_URLS.IMAGEURL}${row.original.Stop1Photo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={`${API_WEB_URLS.IMAGEURL}${row.original.Stop1Photo}`}
                    alt="Pickup Photo"
                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6", cursor: "pointer" }}
                  />
                </a>
                <div style={{ fontSize: "0.65rem", color: "#6c757d", marginTop: 2 }}>Pickup Photo</div>
              </div>
            )}
          </div>
        ),
      },
      {
        Header: "Stop 1 (Drop)",
        accessor: "Stop1Address",
        Cell: ({ row }) => (
          <div style={{ minWidth: "220px" }}>
            <div className="d-flex align-items-start mb-1">
              <i className="fas fa-flag-checkered me-2 text-success mt-1" style={{ fontSize: "0.85rem" }}></i>
              <span className="fw-semibold text-wrap" style={{ fontSize: "0.82rem" }}>
                {row.original.Stop1Address || "-"}
              </span>
            </div>
            {(row.original.Stop1ReceiverName || row.original.Stop1ReceiverMobile) && (
              <div className="d-flex align-items-center mt-1">
                <i className="fas fa-user-check me-2 text-secondary" style={{ fontSize: "0.75rem" }}></i>
                <small className="text-muted">
                  {row.original.Stop1ReceiverName || "-"}{" "}
                  {row.original.Stop1ReceiverMobile && (
                    <span className="text-dark fw-semibold">· {row.original.Stop1ReceiverMobile}</span>
                  )}
                </small>
              </div>
            )}
            <div className="d-flex align-items-center mt-1">
              <i className="fas fa-location-arrow me-2 text-info" style={{ fontSize: "0.7rem" }}></i>
              <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                {row.original.Stop1Latitude || "-"}, {row.original.Stop1Longitude || "-"}
              </small>
            </div>
            {row.original.Stop2Photo && (
              <div className="mt-2">
                <a
                  href={`${API_WEB_URLS.IMAGEURL}${row.original.Stop2Photo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={`${API_WEB_URLS.IMAGEURL}${row.original.Stop2Photo}`}
                    alt="Drop Photo"
                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6", cursor: "pointer" }}
                  />
                </a>
                <div style={{ fontSize: "0.65rem", color: "#6c757d", marginTop: 2 }}>Drop Photo</div>
              </div>
            )}
          </div>
        ),
      },
      {
        Header: "Trip KM",
        accessor: "TotalTripKM",
        Cell: ({ value }) => (
          <div className="text-center">
            <span className="badge bg-primary bg-opacity-10 text-primary fw-semibold" style={{ fontSize: "0.85rem" }}>
              <i className="fas fa-road me-1"></i>
              {value != null ? `${value} km` : "-"}
            </span>
          </div>
        ),
      },
      {
        Header: "Goods",
        accessor: "GoodsType",
        Cell: ({ row }) => (
          <div className="text-center">
            <span className="badge bg-secondary bg-opacity-10 text-secondary fw-semibold d-block mb-1">
              {row.original.GoodsType || "-"}
            </span>
            {row.original.Price != null && (
              <small className="text-success fw-bold">₹ {row.original.Price}</small>
            )}
          </div>
        ),
      },
      {
        Header: "Customer",
        accessor: "UserName",
        Cell: ({ row }) => (
          <div>
            <span className="fw-semibold d-block">{row.original.UserName || "-"}</span>
            <small className="text-muted">{row.original.UserMobileNo || "-"}</small>
          </div>
        ),
      },
      {
        Header: "OTP",
        accessor: "OTP",
        Cell: ({ value }) => (
          <span className={`badge ${value ? "bg-info text-dark" : "bg-light text-muted"} fs-6`}>
            {value ? value.toString().padStart(4, "0") : "-"}
          </span>
        ),
      },
      {
        Header: "Driver",
        accessor: "DriverName",
        Cell: ({ row }) => (
          <div>
            <span className="fw-semibold d-block">{row.original.DriverName || "-"}</span>
            <small className="text-muted">{row.original.DriverMobileNo || "-"}</small>
          </div>
        ),
      },
      {
        Header: "Vehicle",
        accessor: "VehicleName",
        Cell: ({ row }) => (
          <div>
            <div className="d-flex align-items-center mb-1">
              <i className="bx bx-car me-1 text-primary" style={{ fontSize: "1.1rem" }}></i>
              <span className="fw-semibold text-uppercase" style={{ fontSize: "0.82rem" }}>
                {row.original.VehicleName || "-"}
              </span>
            </div>
            {row.original.VehicleCategory && (
              <span className="badge bg-danger bg-opacity-10 text-danger fw-semibold" style={{ fontSize: "0.7rem" }}>
                {row.original.VehicleCategory}
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "Status",
        Cell: ({ value }) => {
          const colorMap = {
            Completed: "bg-success",
            Ended: "bg-success",
            Pending: "bg-warning text-dark",
            Cancelled: "bg-danger",
            Assigned: "bg-info text-dark",
          };
          const cls = colorMap[value] || "bg-light text-dark";
          return (
            <span className={`badge ${cls}`} style={{ fontSize: "0.8rem" }}>
              {value || "-"}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const { F_StatusMaster } = row.original;
          const statusId = Number(F_StatusMaster);
          const isPendingAssignment = statusId === 1;
          const isCancellable = statusId >= 1 && statusId <= 17;

          return (
            <div className="d-flex flex-column gap-1 align-items-center">
              {isPendingAssignment ? (
                <Button
                  className="master-list-assign-btn"
                  onClick={() => btnAssignDriverOnClick(row.original)}
                  size="sm"
                >
                  Assign the Driver
                </Button>
              ) : (
                <span className="text-muted small">Driver assigned</span>
              )}
              {isCancellable && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => btnCancelOnClick(row.original)}
                >
                  Cancel
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [btnAssignDriverOnClick, btnCancelOnClick]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { 
        pageIndex: 0, 
        pageSize: 10,
        sortBy: [
          {
            id: "PickupAddress",
            desc: false
          }
        ]
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div className="master-list-loading d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-muted">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-list-container">
      <Container fluid={true} className="px-3 px-md-4">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />

        {/* Controls Section */}
        <div className="master-list-controls master-list-slide-in mb-4">
          <Row className="align-items-center">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <div className="search-wrapper">
                <FormControl
                  type="text"
                  placeholder="Search booking by pickup, drop, customer..."
                  value={globalFilter || ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="form-control-lg"
                />
              </div>
            </Col>
            <Col md={6} sm={12}>
              <div className="d-flex align-items-center gap-3 justify-content-md-end flex-wrap">
                <ExportButtons title="Booking Report" columns={columns} data={data} filename="Booking_Report" />
                <span className="badge bg-success-subtle text-success border border-success d-inline-flex align-items-center gap-1 px-2 py-2" title={isConnected ? "Real-time SignalR active" : "Connecting..."}>
                  <span className={`spinner-grow spinner-grow-sm ${isConnected ? "text-success" : "text-warning"}`} role="status" style={{ width: 8, height: 8 }} />
                  <span>{isConnected ? "Live Auto-Refresh" : "Connecting..."}</span>
                </span>
                <span className="fw-semibold text-muted">Filter:</span>
                <select
                  className="form-select form-select-lg"
                  style={{ width: "auto" }}
                  value={selectedStatusId}
                  onChange={handleStatusFilterChange}
                >
                  <option value="0">All</option>
                  {statusOptions.map((status) => (
                    <option key={status.Id} value={status.Id}>
                      {status.Name}
                    </option>
                  ))}
                </select>
              </div>
            </Col>
          </Row>
        </div>

        {/* Live New Booking Notification Banner */}
        {newlyAddedId && (
          <div className="alert alert-success alert-dismissible fade show d-flex align-items-center justify-content-between mb-3 py-2 px-3 shadow-sm border border-success" role="alert">
            <div className="d-flex align-items-center">
              <i className="bx bx-bell bx-tada font-size-18 me-2 text-success" />
              <div>
                <strong>⚡ New Ride Booked (#{newlyAddedId})!</strong> Table has been automatically updated in real time.
              </div>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setNewlyAddedId(null)} />
          </div>
        )}

        {/* Table Section */}
        <div className="master-list-table-container master-list-bounce-in mb-4">
          <div className="table-responsive">
            <Table {...getTableProps()} className="master-list-table table-hover">
              <thead style={{ backgroundColor: "#E87B2D", color: "#fff" }}>
                {headerGroups.map((headerGroup, headerGroupIndex) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIndex}>
                    {headerGroup.headers.map((column, columnIndex) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={columnIndex}
                        className="align-middle"
                        style={{ padding: "4px 6px", fontSize: 11, fontWeight: 600, backgroundColor: "#E87B2D", color: "#fff" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-semibold">{column.render("Header")}</span>
                          <span className="sort-indicator" style={{ color: "#fff" }}>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? <i className="fas fa-sort-down" style={{ fontSize: "0.7rem" }}></i>
                                : <i className="fas fa-sort-up" style={{ fontSize: "0.7rem" }}></i>
                              : <i className="fas fa-sort" style={{ opacity: 0.7, fontSize: "0.7rem" }}></i>}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, rowIndex) => {
                  prepareRow(row);
                  const isHighlighted = newlyAddedId && String(row.original?.Id) === String(newlyAddedId);
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={rowIndex}
                      className={`align-middle ${isHighlighted ? "table-warning border-2 border-warning" : ""}`}
                      style={isHighlighted ? { backgroundColor: "#fff3cd", transition: "background 0.5s ease" } : {}}
                    >
                      {row.cells.map((cell, cellIndex) => (
                        <td {...cell.getCellProps()} key={cellIndex} className="py-3">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination Section */}
        <div className="master-list-pagination">
          <Row className="align-items-center">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <div className="master-list-pagination-info d-flex flex-wrap align-items-center gap-3">
                <span className="text-muted">
                  Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                </span>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Go to:</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: '80px' }}
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      gotoPage(page);
                    }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Show:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: '80px' }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Col>
            <Col md={6} sm={12} className="text-md-end">
              <div className="master-list-pagination-controls d-flex gap-2 justify-content-md-end">
                <Button 
                  className="pagination-btn" 
                  onClick={() => gotoPage(0)} 
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-left"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => previousPage()} 
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-left"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => nextPage()} 
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-right"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => gotoPage(pageCount - 1)} 
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-right"></i>
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <Modal
        show={showAssignModal}
        onHide={handleAssignModalClose}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Driver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking ? (
            <>
              <div className="mb-4">
                <h6 className="fw-semibold mb-2">Booking details</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Pickup</span>
                      <div className="fw-semibold mt-1">
                        {selectedBooking.PickupAddress || "-"}
                      </div>
                      <div className="text-muted small mt-2">
                        Lat: {selectedBooking.PickupLatitude || "-"} | Lng:{" "}
                        {selectedBooking.PickupLongitude || "-"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Drop</span>
                      <div className="fw-semibold mt-1">
                        {selectedBooking.DropAddress || "-"}
                      </div>
                      <div className="text-muted small mt-2">
                        Lat: {selectedBooking.DropLatitude || "-"} | Lng:{" "}
                        {selectedBooking.DropLongitude || "-"}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Customer</span>
                      <div className="fw-semibold mt-1">
                        {selectedBooking.UserName || "-"}
                      </div>
                      <div className="text-muted small mt-2">
                        {selectedBooking.UserMobileNo || "-"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Current status</span>
                      <div className="fw-semibold mt-1">{selectedBooking.Status || "-"}</div>
                      <div className="text-muted small mt-2">
                        OTP: {selectedBooking.OTP || "-"}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <div className="border rounded p-3 h-100 border-danger bg-danger bg-opacity-10 text-danger">
                      <span className="text-uppercase small fw-semibold">Vehicle category</span>
                      <div className="fw-bold mt-1">
                        {selectedBooking.VehicleCategory || "-"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded p-3 h-100 border-danger bg-danger bg-opacity-10 text-danger">
                      <span className="text-uppercase small fw-semibold">Weight range</span>
                      <div className="fw-bold mt-1">
                        {selectedBooking.WeightRangeFrom ?? "-"} kg to{" "}
                        {selectedBooking.WeightRangeTo ?? "-"} kg
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <Form.Group controlId="assignDriverSelect" className="mb-3">
                <Form.Label className="fw-semibold">Select driver & vehicle</Form.Label>
                <Form.Select
                  size="lg"
                  value={selectedDriverId}
                  onChange={(event) => setSelectedDriverId(event.target.value)}
                  disabled={!driverOptions.length}
                >
                  {!driverOptions.length && <option value="">No drivers available</option>}
                  {driverOptions.map((driver) => (
                    <option key={driver.Id} value={String(driver.Id)}>
                      {driver.DriverName} · {driver.VehicleRegistrationNo} ·{" "}
                      {driver.VehicleTypeName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {selectedDriverDetails && (
                <div className="border rounded p-3 bg-light">
                  <h6 className="fw-semibold mb-2">Driver & vehicle details</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Driver</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.DriverName || "-"}
                        </div>
                        <div className="text-muted small">
                          {selectedDriverDetails.DriverMobileNo || "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Vehicle</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.VehicleRegistrationNo || "-"}
                        </div>
                        <div className="text-muted small">
                          {selectedDriverDetails.VehicleTypeName || "-"}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-3 mt-1">
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Vehicle No</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.VehicleNo || "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Capacity</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.WeightCapacity ?? "-"} kg
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {!driverOptions.length && (
                <div className="text-center text-muted py-4 border rounded">
                  No driver & vehicle combinations are available right now.
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-4">No booking selected.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleAssignModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignConfirm}
            disabled={!selectedBooking || !selectedDriverId}
          >
            Confirm Assignment
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignDriver;
